import {
  NodeCoreComponent,
  TaintEffect,
  nodeCoreComponentToText,
  taintEffectText,
} from "../../NodeTemplate/types";
import type { ClusterManagerMode, InstanceStatus, NodeNameMode } from "../api/types";
import {
  Kce1MigrationProgressStep,
  Kce1MigrationProgressStatus,
  Kce1MigrationProgressStepStatus,
} from "../types";

export function labelFilterOpText(v: string) {
  switch (v) {
    case "Equal":
      return "=";
    case "NotEqual":
      return "!=";
    case "In":
      return "In";
    case "NotIn":
      return "NotIn";
    case "Exists":
      return "Exists";
    case "DoesNotExist":
      return "DoesNotExist";
    default:
      return "=";
  }
}

export const KecProductGroupId = 100;
export const Kce2ProductGroupId = 278;

export type ClusterType = ClusterManagerMode;
export const CLUSTER_TYPE_INDEPENDENT = "INDEPENDENT_CLUSTER";
export const CLUSTER_TYPE_MANAGED = "MANAGED_CLUSTER";

export const Kce2StandaloneClusterProductTypeId = 692;
export const Kce2ManagedClusterProductTypeId = 1118;
// export const KceStandaloneClusterProductTypeId = 604;

export const kComponentOptions = [
  NodeCoreComponent.ApiServer,
  NodeCoreComponent.Etcd,
  NodeCoreComponent.ControllerManager,
  NodeCoreComponent.EventEtcd,
  NodeCoreComponent.Scheduler,
].map(v => ({
  value: v,
  text: nodeCoreComponentToText(v),
}));

export const kEffectOptions = [
  TaintEffect.NoSchedule,
  TaintEffect.NoExecute,
  TaintEffect.PreferNoSchedule,
].map(v => {
  return {
    text: taintEffectText(v),
    value: v,
  };
});

export const NETWORK_PLUGIN_TYPE_EXCLUSIVE_VPC_CNI = "VPCCNI";
export const NETWORK_PLUGIN_TYPE_CALICO = "CALICO";

export const MIGRATED_PREFIX = "migrated-";

export const VPC_CNI_ADDON_NAME = "vpc-cni";
export const MIGRATED_VPC_CNI_ADDON_NAME = `${MIGRATED_PREFIX}${VPC_CNI_ADDON_NAME}`;
export const EXCLUSIVE_VPC_CNI_ADDON_NAME = "vpc-cni-standalone";

export const DEFAULT_SG_NAME_DISPLAY = "默认安全组-KCE2-{集群ID}";
export const defaultSgName = (clusterId: string) => `默认安全组-KCE2-${clusterId}`;

export const LOG_TYPES = ["ALL_LOG", "CLUSTER_LOG", "NETWORK_LOG", "NODE_LOG"] as const;
export type LogType = (typeof LOG_TYPES)[number];
export function getLogCategory(v: LogType) {
  switch (v) {
    case "CLUSTER_LOG":
      return "CATEGORY_CLUSTER";
    case "NETWORK_LOG":
      return "CATEGORY_NETWORK";
    case "NODE_LOG":
      return "CATEGORY_MACHINE";
    default:
      return undefined;
  }
}
export function getLogTypeText(v: LogType) {
  switch (v) {
    case "CLUSTER_LOG":
      return "集群日志";
    case "ALL_LOG":
      return "全部日志";
    case "NETWORK_LOG":
      return "网络日志";
    case "NODE_LOG":
      return "节点日志";
  }
}
export const LOG_TYPE_OPTIONS = LOG_TYPES.map(v => ({
  text: getLogTypeText(v),
  value: v,
}));

// 伸缩策略
export const stretchPolicyTipMap: Record<string, string> = {
  SCALE_UP_POLICY_RANDOM: "随机选择一个节点池进行扩容",
  SCALE_UP_POLICY_MOST_PODS: "选择能调度更多pod的节点池进行扩容",
  SCALE_UP_POLICY_LEAST_WASTE: "选择调度pod后可用资源剩余更少的节点池进行扩容",
  SCALE_UP_POLICY_LEAST_Priority: "选择已设置优先级最高的节点池进行扩容",
};

// 节点池状态
export const nodePoolStatusMap: Record<string, string> = {
  Running: "运行中",
  Deleting: "删除中",
};

// 节点状态 → 文案的全量映射，展示节点状态文案统一从这里取。
// 注意：后端新增状态值时必须在此补充文案（Record 类型会强制覆盖 InstanceStatus 全部成员），
// 但新增状态默认不加入筛选项，是否加入筛选项由产品决定（见 INSTANCE_STATUS_FILTER_STATUSES）。
export const INSTANCE_STATUS_MAP: Record<InstanceStatus, string> = {
  Ready: "正常",
  NotReady: "未就绪",
  InstanceCreating: "创建中",
  InstanceReinstalling: "重装中",
  InstanceCreateFailed: "实例创建失败",
  InstanceError: "实例异常",
  Installing: "安装中",
  Deleting: "删除中",
  Removing: "移出中",
  ForceRemoving: "强制移出中",
  DeleteError: "删除失败",
  RemoveError: "移出超时",
  Disabled: "欠费关停",
  WaitScriptExecute: "待连接",
  ReinstallFailed: "重装失败",
};

// 节点状态筛选项的固定清单，顺序与取值由产品决定。
// 注意：值不要求与 INSTANCE_STATUS_MAP 一一对应——
// map 后续可能新增状态值，但不加入筛选项；何时加入、以什么顺序展示，由产品明确决定后再改这里。
// label 复用 INSTANCE_STATUS_MAP 的文案，不要在此处重复写字面量。
const INSTANCE_STATUS_FILTER_STATUSES = [
  "Ready",
  "NotReady",
  "InstanceCreating",
  "InstanceReinstalling",
  "ReinstallFailed",
  "InstanceCreateFailed",
  "InstanceError",
  "Installing",
  "Deleting",
  "Removing",
  "ForceRemoving",
  "DeleteError",
  "RemoveError",
  "Disabled",
  "WaitScriptExecute",
] as const satisfies readonly InstanceStatus[];

export const INSTANCE_STATUS_FILTER_OPTIONS = INSTANCE_STATUS_FILTER_STATUSES.map(value => ({
  label: INSTANCE_STATUS_MAP[value],
  value,
}));

// 节点池 Reason：安全组被删除
export const SECURITY_GROUP_DELETE_REASON = "SecurityGroupNotFound";

// 安全组被删除时的禁用文案（开启弹性伸缩、调节节点数量复用）
export const SECURITY_GROUP_DELETED_TIP =
  "当前节点池配置中存在被删除的安全组，请重新配置安全组后再次开启弹性伸缩";

// 节点池状态栏 warning 文案
export const SECURITY_GROUP_DELETED_STATUS_TIP =
  "当前节点池配置中存在被删除的安全组，请重新配置安全组，否则将影响节点池扩容";

// 子网策略
export const subnetStrategyMap: Record<string, string> = {
  Order: "顺序",
  Random: "随机",
  "": "-",
};

export const BUY_TIME_TO_MONTH = [
  // placeholder
  0,
  // 1-11 month
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11,
  // 1-3 year
  12, 24, 36,
];

export const DEFAULT_NODE_NAME_TYPE: NodeNameMode = "INTERNAL_IP";

export const DEFAULT_NODE_DELETING_PROTECTION = false;

export const InputTypeTransMap: Record<string, string> = {
  container_stdout: "容器标准输出",
  container_file: "容器文件路径",
  host_file: "主机文件路径",
};

export const LOG_ADDON_NAME = "kce-filebeat";
export const MIGRATED_LOG_ADDON_NAME = `${MIGRATED_PREFIX}${LOG_ADDON_NAME}`;
export const EVENT_ADDON_NAME = "event-device-plugin";
export const MIGRATED_EVENT_ADDON_NAME = `${MIGRATED_PREFIX}${EVENT_ADDON_NAME}`;
export const ETCD_BACKUP_ADDON_NAME = "etcd-backup-operator";
export const MIGRATED_ETCD_BACKUP_ADDON_NAME = `${MIGRATED_PREFIX}${ETCD_BACKUP_ADDON_NAME}`;
export const KUBE_PROXY_ADDON_NAME = "kube-proxy";
export const MIGRATED_KUBE_PROXY_ADDON_NAME = `${MIGRATED_PREFIX}${KUBE_PROXY_ADDON_NAME}`;

export const ADDON_INSTALL_DENY_LIST = new Set([
  LOG_ADDON_NAME,
  EVENT_ADDON_NAME,
  ETCD_BACKUP_ADDON_NAME,
  VPC_CNI_ADDON_NAME,
]);

export const VPC_ROUTE_CONTROLLER_ADDON_NAME = "vpc-route-controller";

export const NETWORKPOLICY_ADDON_NAME = "networkpolicy";
export const NETWORKPOLICY_VPCCNI_UNSUPPORTED_VERSION = "1.4.0";

// 安装后禁止卸载的组件列表
export const ADDON_UNINSTALL_DENY_LIST = new Set<string>([VPC_ROUTE_CONTROLLER_ADDON_NAME]);

export const MSP_LEGACY_DATETIME = "2025-07-18";

// TODO: i18n
export const AUTO_SCALE_DAY_TRANS_MAP = {
  Monday: "周一",
  Tuesday: "周二",
  Wednesday: "周三",
  Thursday: "周四",
  Friday: "周五",
  Saturday: "周六",
  Sunday: "周日",
};

export const DEFAULT_AUTO_SCALE_RULES = {
  APIServerCPU: 70,
  APIServerMem: 80,
  ControlManagerCPU: 70,
  ControlManagerMem: 80,
  SchedulerCPU: 70,
  SchedulerMem: 80,
  EtcdCPU: 70,
  EtcdMem: 80,
};

/**
 * 1 个月
 */
export const DEFAULT_KUBE_CONFIG_EXPIRE_DURATION = 24 * 30;

export const KCE1_MIGRATION_PROGRESS_STATUS_TRANS_MAP: Record<Kce1MigrationProgressStatus, string> =
  {
    [Kce1MigrationProgressStatus.Migrating]: "迁移中",
    [Kce1MigrationProgressStatus.Succeed]: "迁移成功",
    [Kce1MigrationProgressStatus.Timeout]: "迁移超时",
    [Kce1MigrationProgressStatus.Failed]: "迁移失败",
  };

export function getMigrationStatusText(
  v: Kce1MigrationProgressStatus | (string & {}),
  defaultValue = "",
) {
  return KCE1_MIGRATION_PROGRESS_STATUS_TRANS_MAP[v as Kce1MigrationProgressStatus] ?? defaultValue;
}

export const KCE1_MIGRATION_PROGRESS_STEP_STATUS_TRANS_MAP: Record<
  Kce1MigrationProgressStepStatus,
  string
> = {
  [Kce1MigrationProgressStepStatus.Success]: "完成",
  [Kce1MigrationProgressStepStatus.Failed]: "异常",
  [Kce1MigrationProgressStepStatus.Running]: "进行中",
  [Kce1MigrationProgressStepStatus.Pending]: "等待中",
};

export function getMigrationStepStatusText(
  status: Kce1MigrationProgressStepStatus | (string & {}),
  defaultValue = "",
) {
  return (
    KCE1_MIGRATION_PROGRESS_STEP_STATUS_TRANS_MAP[status as Kce1MigrationProgressStepStatus] ??
    defaultValue
  );
}

export const KCE1_MIGRATION_PROGRESS_STEP_TRANS_MAP: Record<Kce1MigrationProgressStep, string> = {
  [Kce1MigrationProgressStep.EnvironmentPreparation]: "环境准备",
  [Kce1MigrationProgressStep.CertificateMigration]: "证书迁移",
  [Kce1MigrationProgressStep.NodeResourceMigration]: "节点资源迁移",
  [Kce1MigrationProgressStep.NetworkMigration]: "网络迁移",
  [Kce1MigrationProgressStep.ControlPlaneMigration]: "控制面迁移",
  [Kce1MigrationProgressStep.PostMigrationCleanup]: "后置资源清理",
};

export function getMigrationStepText(
  step: Kce1MigrationProgressStep | (string & {}),
  defaultValue = "",
) {
  return KCE1_MIGRATION_PROGRESS_STEP_TRANS_MAP[step as Kce1MigrationProgressStep] ?? defaultValue;
}
